import { useEffect, useMemo, useRef, useState } from 'react';
import { divIcon } from 'leaflet';
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import { getMapboxTileLayer } from '../utils/mapbox';
import {
  DEFAULT_FLEET_ZOOM,
  getFleetLocationFreshness,
  getVisibleFleetDrivers,
} from '../utils/liveFleet';

const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  "'": '&#39;',
  '"': '&quot;',
}[character]));

const makeTaxiIcon = (cabNumber, availability, onTrip = false, freshnessState = 'unknown', heading = 0) => {
  const online = availability === 'Online';
  const stateClass = onTrip ? 'dmap-ontrip' : online ? 'dmap-online' : 'dmap-offline';
  const body = onTrip ? '#ef4444' : online ? '#fbbf24' : '#38bdf8';
  const stroke = onTrip ? '#7f1d1d' : online ? '#78350f' : '#075985';
  const roof = onTrip ? '#991b1b' : online ? '#1d4ed8' : '#0f766e';
  const dot = onTrip ? '#ef4444' : online ? '#34d399' : '#38bdf8';

  return divIcon({
    className: '',
    html: `<div class="dmap-marker ${stateClass} dmap-freshness-${freshnessState}">
      <span class="dmap-cab-label">Cab #${escapeHtml(cabNumber || '?')}</span>
      <div class="dmap-vehicle-wrap">
        <svg xmlns="http://www.w3.org/2000/svg" width="44" height="64" viewBox="0 0 44 64" aria-hidden="true" style="transform:rotate(${Number(heading) || 0}deg);transform-origin:22px 32px;transition:transform .4s linear">
          <ellipse cx="22" cy="60" rx="13" ry="4" fill="rgba(0,0,0,0.28)"/>
          <rect x="5" y="5" width="34" height="48" rx="11" fill="${body}" stroke="${stroke}" stroke-width="1.5"/>
          <rect x="9" y="10" width="26" height="13" rx="5" fill="rgba(186,230,253,0.82)" stroke="${stroke}" stroke-width="0.7"/>
          <rect x="9" y="35" width="26" height="10" rx="4" fill="rgba(186,230,253,0.6)" stroke="${stroke}" stroke-width="0.7"/>
          <rect x="15" y="25" width="14" height="8" rx="2" fill="${roof}"/>
          <text x="22" y="31.5" text-anchor="middle" fill="#fff" font-size="5.5" font-family="Arial,sans-serif" font-weight="800" letter-spacing="0.4">TAXI</text>
          <rect x="0" y="13" width="5" height="11" rx="2.5" fill="#1e293b"/>
          <rect x="39" y="13" width="5" height="11" rx="2.5" fill="#1e293b"/>
          <rect x="0" y="37" width="5" height="11" rx="2.5" fill="#1e293b"/>
          <rect x="39" y="37" width="5" height="11" rx="2.5" fill="#1e293b"/>
        </svg>
        <span class="dmap-status-dot" style="background:${dot}"></span>
      </div>
    </div>`,
    iconSize: [80, 108],
    iconAnchor: [40, 96],
    popupAnchor: [0, -100],
  });
};

function BoundsAutoFit({ positions, serviceCenter, serviceZoom }) {
  const map = useMap();
  const fitted = useRef(false);

  useEffect(() => {
    if (fitted.current) return;
    fitted.current = true;
    if (!positions.length) {
      map.setView(serviceCenter, serviceZoom);
    } else if (positions.length === 1) {
      map.setView(positions[0], Math.max(serviceZoom, DEFAULT_FLEET_ZOOM));
    } else {
      map.fitBounds(positions, { padding: [48, 48], maxZoom: 15 });
      if (map.getZoom() < serviceZoom) map.setZoom(serviceZoom);
    }
  }, [map, positions, serviceCenter, serviceZoom]);

  return null;
}

function FollowCab({ driver }) {
  const map = useMap();
  useEffect(() => {
    if (driver?.position) map.panTo(driver.position, { animate: true, duration: 0.7 });
  }, [driver?.position, map]);
  return null;
}

function SmoothMarker({ driver, icon, children, onFollow }) {
  const markerRef = useRef(null);
  const previous = useRef(driver.position);
  useEffect(() => {
    const marker = markerRef.current;
    const from = previous.current;
    const to = driver.position;
    previous.current = to;
    if (!marker || !from || !to) return undefined;
    const started = performance.now();
    let frame;
    const animate = (now) => {
      const progress = Math.min(1, (now - started) / 900);
      const eased = 1 - ((1 - progress) ** 3);
      marker.setLatLng([from[0] + ((to[0] - from[0]) * eased), from[1] + ((to[1] - from[1]) * eased)]);
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [driver.position]);
  return <Marker ref={markerRef} position={driver.position} icon={icon} eventHandlers={{ click: onFollow }}>{children}</Marker>;
}

function ServiceAreaCenter({ center, zoom }) {
  const map = useMap();

  useEffect(() => {
    if (center) map.setView(center, zoom);
  }, [center, map, zoom]);

  return null;
}

const LiveFleetMap = ({
  drivers = [],
  loading = false,
  error = '',
  freshnessNow = Date.now(),
  serviceCenter,
  serviceZoom = DEFAULT_FLEET_ZOOM,
  filter = 'all',
  variant = 'full',
}) => {
  const [followId, setFollowId] = useState('');
  const compact = variant === 'compact';
  const mapboxTiles = useMemo(() => getMapboxTileLayer(), []);
  const visibleDrivers = useMemo(
    () => getVisibleFleetDrivers({ drivers, filter, serviceCenter }),
    [drivers, filter, serviceCenter],
  );
  const positions = useMemo(
    () => visibleDrivers.map((driver) => driver.position).filter(Boolean),
    [visibleDrivers],
  );
  const showMap = !loading || drivers.length > 0;
  const followedDriver = visibleDrivers.find((driver) => String(driver.driverId) === followId) || null;
  const lostSignalCount = visibleDrivers.filter((driver) => getFleetLocationFreshness(driver.updatedAt, freshnessNow, driver.motionState).state === 'warning' || driver.trackingHealth === 'lost').length;

  return (
    <div
      className={`live-fleet-map live-fleet-map--${variant}`}
      role="region"
      aria-label={compact ? 'Compact live fleet map' : 'Live dispatch fleet map'}
    >
      {showMap ? (
        <MapContainer
          center={serviceCenter}
          zoom={serviceZoom}
          style={{ width: '100%', height: '100%' }}
          zoomControl={compact}
          scrollWheelZoom={!compact}
        >
          {mapboxTiles ? (
            <TileLayer url={mapboxTiles.url} attribution={mapboxTiles.attribution} />
          ) : (
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
          )}
          <ServiceAreaCenter center={serviceCenter} zoom={serviceZoom} />
          <BoundsAutoFit positions={positions} serviceCenter={serviceCenter} serviceZoom={serviceZoom} />
          {followedDriver ? <FollowCab driver={followedDriver} /> : null}

          {visibleDrivers.map((driver) => {
            const name =
              [driver.firstName, driver.lastName].filter(Boolean).join(' ') ||
              `Driver ${driver.driverId || '—'}`;
            const freshness = getFleetLocationFreshness(driver.updatedAt, freshnessNow, driver.motionState);
            return (
              <SmoothMarker
                key={driver._id || `${driver.driverId}-${driver.cabNumber}`}
                driver={driver}
                icon={makeTaxiIcon(driver.cabNumber, driver.availability, driver.onTrip, freshness.state, driver.heading)}
                onFollow={() => setFollowId((current) => current === String(driver.driverId) ? '' : String(driver.driverId))}
              >
                <Popup>
                  <div className="dmap-popup">
                    <strong className="dmap-popup__name">{name}</strong>
                    <span className="dmap-popup__cab">Cab #{driver.cabNumber || '—'}</span>
                    <span className={`dmap-popup__avail ${driver.availability === 'Online' ? 'is-online' : 'is-offline'}`}>
                      {driver.availability === 'Online' && driver.onTrip
                        ? 'Online — On Trip'
                        : driver.availability}
                    </span>
                    <span className="dmap-popup__time">Last ping: {freshness.label}</span>
                    <span className="dmap-popup__time">Speed: {(Number(driver.speed || 0) * 2.23694).toFixed(0)} mph · GPS ±{Math.round(Number(driver.accuracy || 0))} m</span>
                    <span className="dmap-popup__time">Tracking: {driver.trackingHealth === 'lost' || freshness.state === 'warning' ? 'Lost signal' : driver.trackingHealth === 'degraded' ? 'Degraded' : 'Healthy'} · Seq {driver.sequence || '—'}</span>
                    <button type="button" onClick={() => setFollowId((current) => current === String(driver.driverId) ? '' : String(driver.driverId))}>{followId === String(driver.driverId) ? 'Stop following' : 'Follow cab'}</button>
                  </div>
                </Popup>
              </SmoothMarker>
            );
          })}
          {visibleDrivers.map((driver) => driver.breadcrumbs?.length > 1 ? <Polyline key={`trail-${driver.driverId}`} positions={driver.breadcrumbs} pathOptions={{ color: driver.onTrip ? '#ef4444' : '#2563eb', weight: 3, opacity: 0.55 }} /> : null)}
        </MapContainer>
      ) : null}

      {loading && !drivers.length ? (
        <div className="dmap-overlay-center">
          <div className="dmap-loading-card">
            <span className="dmap-live-pulse" />
            Loading vehicles…
          </div>
        </div>
      ) : null}

      {!loading && error && !drivers.length ? (
        <div className="dmap-overlay-center">
          <div className="dmap-error-card">{error}</div>
        </div>
      ) : null}

      {!loading && !error && !positions.length ? (
        <div className="dmap-overlay-center dmap-overlay-center--empty">
          <div className="dmap-loading-card">No vehicles are reporting a usable location.</div>
        </div>
      ) : null}

      {error && drivers.length ? (
        <div className="live-fleet-map__notice" role="status">Showing the last loaded fleet positions.</div>
      ) : null}
      {lostSignalCount > 0 ? <div className="live-fleet-map__notice" role="alert" style={{ background: '#7f1d1d', color: '#fff' }}>{lostSignalCount} cab{lostSignalCount === 1 ? '' : 's'} with lost or stale GPS signal</div> : null}
    </div>
  );
};

export default LiveFleetMap;
